import { useEffect, useRef, useState } from "react";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import { AiOutlinePlus } from "react-icons/ai";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { useGetOvertime } from "../../../../services/overtime.service";
import { OvertimeOrderBy } from "../../../../models/overtime.dto";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../components/search/APISearchInput";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import OvertimeManagementModalForm from "../modals/OvertimeManagementModalForm";
import { EmployeeOvertimeEntryDto } from "../../../../models/employee-overtime-entry.dto";
import {
	useGetEmployeeOvertimeEntry,
	useDeleteEmployeeOvertimeEntry,
} from "../../../../services/employee-overtime-entry.service";
import {
	overtimeManagementColumns,
	overtimeManagementColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";

const OvertimeManagementView = () => {
	const { currentCompany } = useCompanyStore();
	const { currentUser, userType } = useUserStore();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(overtimeManagementColumnVisibility);

	// const { mutate: searchEmployeeOvertimeEntries } = useGetEmployeeOvertimeEntrySearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<EmployeeOvertimeEntryDto>({
		fetchHook: useGetEmployeeOvertimeEntry,
		options: {
			companyId: currentCompany?.id,
			queryMode: "ALL",
		},
	});

	// Fetched data for select fields requiring data from database
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: overtimeTypes } = useGetOvertime({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: OvertimeOrderBy.NAME_ASC,
	});
	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteOvertimeEntry } = useDeleteEmployeeOvertimeEntry();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteOvertimeEntry(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			}
		);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					{/* <APISearchInput 
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={undefined as any}
					/> */}
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
									{
										title: "Date Modified",
										fieldName: "modifiedAt",
									},
								],
							},
							{
								title: "Employee",
								fieldName: "employeeId",
								fieldType: "SELECT_FIELD",
								options: employees?.data || [],
								mapTexts: ["firstName", "lastName"],
							},
							{
								title: "Overtime Type",
								fieldName: "overtimeId",
								fieldType: "SELECT_FIELD",
								options: overtimeTypes?.data || [],
							},
							{
								title: "Pay Period",
								fieldName: "payPeriodId",
								fieldType: "SELECT_FIELD",
								options: payPeriods?.data || [],
								mapText: "code",
							},
						]}
					/>
					<ManageTableColumns
						columns={overtimeManagementColumns}
						defaultVisibilityModel={overtimeManagementColumnVisibility}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<div
					className="flex items-center text-primary-blue cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-base" />
					<span className="text-sm">Add New Entry</span>
				</div>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.OVERTIME_MANAGEMENT}
				data={tableData}
				columns={overtimeManagementColumns}
				columnVisibility={columnVisibilityModel} // ✅ Add this
				onColumnVisibilityChange={setColumnVisibilityModel} // ✅ Add this
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={overtimeManagementColumnVisibility}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={setAnchorEl}
			/> */}
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={`${formDataValues?.employee?.firstName}'s entry` || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<OvertimeManagementModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Entry" : "Create Entry"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					employees={employees?.data || []}
					overtimeTypes={overtimeTypes?.data || []}
					payPeriods={payPeriods?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
		</>
	);
};

export default OvertimeManagementView;
